/**
 * Document Viewer — shared preview for PDFs, Office docs, images and text.
 *
 * Two entry points:
 *   openViewer(opts)            — full modal (chrome + body), used by apps
 *                                 that don't own their own modal.
 *   mountPreview(el, opts)      — renders JUST the preview body into an
 *                                 element, for apps with their own chrome
 *                                 (intranet documents, vault KB, induction).
 *
 * ── Why PDFs render to canvas rather than an <iframe> ──────────────────
 * Chrome on Android has no built-in PDF viewer, so `<iframe src="…pdf">`
 * renders a BLANK panel — no error, no fallback. Every modal PDF preview
 * in the estate was dead on mobile (reproduced on Pixel 5, 2026-07-31).
 * PDF.js renders to <canvas>, which works identically on desktop and
 * mobile, so there is one code path rather than a device sniff.
 *
 * ── Why Office docs need a server-rendered PDF ─────────────────────────
 * We used to point Office files at view.officeapps.live.com. That needs
 * MICROSOFT's servers to fetch the document from a public URL, so it (a)
 * handed a live user JWT to a third party in a query string and (b) did
 * not work — it returned errorpage.htm for our documents. Callers now
 * pass `previewUrl`, a same-origin endpoint that converts to PDF with
 * headless LibreOffice and caches the result; it then renders through the
 * same PDF.js path. No third party, no credential leak.
 *
 * Pages render lazily via IntersectionObserver — a 200-page policy
 * document must not rasterise 200 canvases to show page 1.
 */

import { t } from '../i18n/index.js';
import { localeHeaders } from '../services/api.js';

const PDF_EXTS = ['pdf'];
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf'];
const TEXT_EXTS = ['txt', 'csv', 'log', 'md'];

// Cap the backing-store resolution. devicePixelRatio is 3+ on modern
// phones; a full-width A4 page at 3x is ~25 MB of canvas and Safari kills
// the tab. 2x is indistinguishable at these sizes.
const MAX_PIXEL_RATIO = 2;

let _pdfjs = null;

/** Load PDF.js once, lazily — it is a large chunk and most page views never preview. */
async function getPdfjs() {
  if (_pdfjs) return _pdfjs;
  const pdfjs = await import('pdfjs-dist');
  // Same-origin module worker emitted by the bundler. Deliberately NOT a
  // blob: worker — CSP across the estate is `default-src 'self'` with no
  // worker-src, so a blob worker would be refused.
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url,
  ).href;
  _pdfjs = pdfjs;
  return pdfjs;
}

function extOf(filename, url) {
  return String(filename || url || '')
    .split('?')[0]
    .split('.')
    .pop()
    .toLowerCase();
}

function escHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// `url` is required: X-ISG-Locale goes only to our own origin (see
// localeHeaders), so a failed preview's server message is in the reader's language.
function fetchOpts(token, url) {
  const headers = { ...localeHeaders(url) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // credentials:'include' carries the httpOnly auth cookie — the primary
  // carrier post-Phase-B. The token header is the legacy fallback.
  return { headers, credentials: 'include' };
}

function spinner() {
  return '<div class="dv-loading"><div class="spinner"></div></div>';
}

function fallback(filename, message, detail) {
  return `
    <div class="doc-viewer-fallback">
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/></svg>
      <p><strong>${escHtml(filename || t('viewer.document'))}</strong></p>
      <p class="text-muted">${escHtml(message)}</p>
      ${detail ? `<p class="text-muted" style="font-size:0.8rem">${escHtml(detail)}</p>` : ''}
    </div>`;
}

/**
 * Render a PDF into `container` as a lazily-rasterised column of canvases.
 * Returns a teardown function.
 */
async function renderPdf(container, source, token) {
  const pdfjs = await getPdfjs();
  const params = typeof source === 'string'
    ? { url: source, withCredentials: true, httpHeaders: token ? { Authorization: `Bearer ${token}` } : {} }
    : { data: source };

  const doc = await pdfjs.getDocument(params).promise;

  container.innerHTML = '';
  const pagesEl = document.createElement('div');
  pagesEl.className = 'dv-pages';
  container.appendChild(pagesEl);

  const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
  const width = () => Math.max(pagesEl.clientWidth || container.clientWidth || 600, 280);

  const rendered = new Set();
  const holders = [];

  for (let n = 1; n <= doc.numPages; n++) {
    const holder = document.createElement('div');
    holder.className = 'dv-page';
    holder.dataset.page = String(n);
    // Placeholder ratio (A4 portrait) so the scrollbar is roughly right
    // before a page has been measured — avoids the scroll position
    // jumping around as pages resolve.
    holder.style.aspectRatio = '1 / 1.414';
    pagesEl.appendChild(holder);
    holders.push(holder);
  }

  async function renderPage(holder) {
    const n = Number(holder.dataset.page);
    if (rendered.has(n)) return;
    rendered.add(n);
    try {
      const page = await doc.getPage(n);
      const base = page.getViewport({ scale: 1 });
      const scale = width() / base.width;
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width * ratio);
      canvas.height = Math.floor(viewport.height * ratio);
      canvas.style.width = '100%';
      canvas.style.height = 'auto';
      holder.style.aspectRatio = `${viewport.width} / ${viewport.height}`;

      const ctx = canvas.getContext('2d', { alpha: false });
      await page.render({
        canvasContext: ctx,
        viewport,
        transform: ratio === 1 ? undefined : [ratio, 0, 0, ratio, 0, 0],
      }).promise;

      holder.innerHTML = '';
      holder.appendChild(canvas);
    } catch (err) {
      rendered.delete(n);
      holder.innerHTML = `<div class="dv-page-error">${escHtml(t('viewer.page_failed', { page: n }))}</div>`;
    }
  }

  // rootMargin pre-renders one screen ahead so scrolling feels instant.
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) renderPage(e.target); });
  }, { root: container, rootMargin: '200% 0px' });
  holders.forEach((h) => io.observe(h));

  // First page immediately — don't wait for the observer's first tick.
  await renderPage(holders[0]);

  // Re-rasterise at the new width on resize/orientation change, but only
  // pages already drawn (debounced — resize fires continuously).
  let resizeTimer = null;
  const onResize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      const toRedraw = [...rendered];
      rendered.clear();
      toRedraw.forEach((n) => {
        const h = holders[n - 1];
        if (h) renderPage(h);
      });
    }, 250);
  };
  window.addEventListener('resize', onResize);

  return () => {
    clearTimeout(resizeTimer);
    window.removeEventListener('resize', onResize);
    io.disconnect();
    doc.destroy();
  };
}

/**
 * Render a document preview into `el`. Returns a teardown function.
 *
 * @param {HTMLElement} el
 * @param {Object}   opts
 * @param {string}   opts.url          — the raw document URL (also used for download)
 * @param {string}  [opts.previewUrl]  — same-origin PDF rendition, REQUIRED for Office types
 * @param {string}  [opts.filename]    — used for type detection + the download name
 * @param {string}  [opts.token]       — legacy JWT carrier; cookies are used regardless
 */
export function mountPreview(el, { url, previewUrl, filename, token } = {}) {
  const ext = extOf(filename, url);
  let teardown = null;
  let disposed = false;

  const setTeardown = (fn) => {
    if (disposed) { try { fn && fn(); } catch (_) { /* noop */ } return; }
    teardown = fn;
  };

  el.innerHTML = spinner();

  if (PDF_EXTS.includes(ext) || (OFFICE_EXTS.includes(ext) && previewUrl)) {
    const src = OFFICE_EXTS.includes(ext) ? previewUrl : url;
    fetch(src, fetchOpts(token, src))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.arrayBuffer();
      })
      .then((buf) => renderPdf(el, new Uint8Array(buf), token))
      .then(setTeardown)
      .catch((err) => {
        el.innerHTML = fallback(
          filename,
          t('viewer.no_preview_document'),
          err && err.message,
        );
      });
  } else if (IMAGE_EXTS.includes(ext)) {
    fetch(url, fetchOpts(token, url))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        // dv-* namespace deliberately: several apps define .doc-viewer-image
        // as a WRAPPER with an <img> inside, so reusing that name here
        // silently applies wrapper rules (display:flex, padding) to a
        // replaced element.
        el.innerHTML = `<div class="dv-image"><img src="${blobUrl}" alt="${escHtml(filename || t('viewer.image'))}"></div>`;
        setTeardown(() => URL.revokeObjectURL(blobUrl));
      })
      .catch((err) => {
        el.innerHTML = fallback(filename, t('viewer.no_preview_image'), err && err.message);
      });
  } else if (TEXT_EXTS.includes(ext)) {
    fetch(url, fetchOpts(token, url))
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((text) => {
        el.innerHTML = `<div class="dv-text"><pre>${escHtml(text)}</pre></div>`;
      })
      .catch((err) => {
        el.innerHTML = fallback(filename, t('viewer.no_preview_file'), err && err.message);
      });
  } else if (OFFICE_EXTS.includes(ext)) {
    // No previewUrl supplied — the caller's app hasn't wired the
    // server-side rendition endpoint yet. Say so honestly rather than
    // showing a broken third-party frame.
    el.innerHTML = fallback(filename, t('viewer.no_preview_office'));
  } else {
    el.innerHTML = fallback(filename, t('viewer.no_preview_type'));
  }

  return () => {
    disposed = true;
    if (teardown) { try { teardown(); } catch (_) { /* noop */ } }
    teardown = null;
  };
}

// ── Modal wrapper ─────────────────────────────────────────────────────

let modalEl = null;
let currentDownloadHandler = null;
let currentTeardown = null;

function ensureModal() {
  if (modalEl) return modalEl;
  modalEl = document.createElement('div');
  modalEl.className = 'doc-viewer-overlay';
  modalEl.style.display = 'none';
  modalEl.innerHTML = `
    <div class="doc-viewer-modal">
      <div class="doc-viewer-header">
        <div class="doc-viewer-title" id="dv-title"></div>
        <div class="doc-viewer-actions">
          <button class="btn btn-primary btn-sm" id="dv-download"></button>
          <button class="btn btn-ghost btn-sm" id="dv-close">&times;</button>
        </div>
      </div>
      <div class="doc-viewer-body" id="dv-body"></div>
    </div>
  `;
  document.body.appendChild(modalEl);

  modalEl.querySelector('#dv-close').addEventListener('click', closeViewer);
  modalEl.addEventListener('click', (e) => { if (e.target === modalEl) closeViewer(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modalEl.style.display !== 'none') closeViewer();
  });

  return modalEl;
}

/**
 * Open the shared document viewer modal.
 * @param {Object}  opts               — see mountPreview, plus:
 * @param {string} [opts.title]        — header text (defaults to filename)
 */
export function openViewer({ url, previewUrl, filename, title, token }) {
  const modal = ensureModal();
  const body = modal.querySelector('#dv-body');
  const titleEl = modal.querySelector('#dv-title');
  const downloadBtn = modal.querySelector('#dv-download');

  titleEl.textContent = title || filename || t('viewer.document');
  // Labelled on every OPEN, not when the modal is first built: the modal is
  // created once and reused, and its chrome must follow the current locale.
  downloadBtn.textContent = t('common.download');
  modal.querySelector('#dv-close').setAttribute('aria-label', t('common.close'));

  if (currentDownloadHandler) downloadBtn.removeEventListener('click', currentDownloadHandler);
  currentDownloadHandler = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const btn = e.currentTarget;
    const origText = btn.textContent;
    btn.textContent = t('common.downloading');
    btn.disabled = true;
    try {
      const res = await fetch(url, fetchOpts(token, url));
      // i18n-ignore: logged to the console only, never shown to the reader
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      btn.textContent = origText;
      btn.disabled = false;
    }
  };
  downloadBtn.addEventListener('click', currentDownloadHandler);

  if (currentTeardown) { try { currentTeardown(); } catch (_) { /* noop */ } }
  currentTeardown = mountPreview(body, { url, previewUrl, filename, token });

  modal.style.display = 'flex';
  document.body.style.overflow = 'hidden';
}

/** Close the viewer and release canvases / blob URLs. */
export function closeViewer() {
  if (!modalEl) return;
  modalEl.style.display = 'none';
  document.body.style.overflow = '';
  if (currentTeardown) { try { currentTeardown(); } catch (_) { /* noop */ } }
  currentTeardown = null;
  const body = modalEl.querySelector('#dv-body');
  if (body) body.innerHTML = '';
}
